import { Button, Icon, IconButton, makeStyles, Theme } from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { capitalizeAll, capitalizeFirst, useScreenSizeCheck } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties } from 'react';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectProject } from '../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            width         : '100%',
            flexWrap      : 'wrap',
        },

        leftControls: {
            flex          : 1,
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            width         : '100%',
            flexWrap      : 'wrap',
            '& $input'    : {
                marginRight: theme.spacing(1),
            },
        },

        rightControls: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-end',
            flexWrap      : 'wrap',
            '& $input'    : {
                marginLeft: theme.spacing(1),
            },
        },

        inputsContainer: {
            display      : 'flex',
            flexDirection: 'column',
        },

        inputs: {
            display      : 'flex',
            flexDirection: 'row',
            flexWrap     : 'wrap',
        },

        inputErrors: {
            marginLeft: theme.spacing(2),
            marginTop : theme.spacing(1),
            color     : theme.palette.error.main,
        },

        input: {
            '& .MuiOutlinedInput-input': {
                paddingTop   : theme.spacing(1.5),
                paddingBottom: theme.spacing(0.5),
            },
        },

        dateInputWrapper: {
            position             : 'relative',
            '&.has-remove-button': {
                marginRight: theme.spacing(2),
            },
        },

        dateInput: {
            width: '16em',
        },

        label: {
            textTransform: 'capitalize',
            fontWeight   : 'bold',
        },


        [theme.breakpoints.up('md')]: {
            input: {
                marginRight: theme.spacing(2),
            },
        },
    };
}, { name: 'TimeRangeControlBar', index: 1 });



export interface TimeRangeControlBarProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // Input values provided by parent container. The parent container will be in control of the values. This component is a dumb
    // component and will not have any logic to do anything with these value other than display them.
    readonly startTime: number;
    readonly endTime: number;
    readonly inputsError?: string | undefined;

    readonly enablePrevButton?: boolean | undefined;
    readonly enableNextButton?: boolean | undefined;

    readonly onStartTimeChanged: (timestamp: MaterialUiPickersDate)=> void;
    readonly onEndTimeChanged: (timestamp: MaterialUiPickersDate)=> void;
    readonly onPrevButtonClicked?: ()=> void | undefined;
    readonly onNextButtonClicked?: ()=> void | undefined;
}


export const TimeRangeControlBar: React.FC<TimeRangeControlBarProps> = (props: TimeRangeControlBarProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const isTabletOrLarger = useScreenSizeCheck('md');


    return (
        <div className={clsx(props.className, classes.root)}>
            <div className={classes.leftControls}>
                <div className={classes.inputsContainer}>
                    <div className={classes.inputs}>
                        <div className={clsx(classes.dateInputWrapper)}>
                            <KeyboardDateTimePicker
                                className={clsx(classes.input, classes.dateInput)}
                                label={capitalizeAll(trans('label.start_date'))}
                                inputVariant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                allowKeyboardControl
                                ampm={false}
                                value={props.startTime}
                                format="YYYY/MM/DD HH:mm"
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                invalidDateMessage=""
                                minDateMessage=""
                                maxDateMessage=""
                                showTodayButton
                                disableFuture
                                onChange={(date: MaterialUiPickersDate) => {
                                    props.onStartTimeChanged(date);
                                }}
                            />
                        </div>
                        <div className={clsx(classes.dateInputWrapper)}>
                            <KeyboardDateTimePicker
                                className={clsx(classes.input, classes.dateInput)}
                                label={capitalizeAll(trans('label.end_date'))}
                                inputVariant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                allowKeyboardControl
                                ampm={false}
                                value={props.endTime}
                                format="YYYY/MM/DD HH:mm"
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                invalidDateMessage=""
                                minDateMessage=""
                                maxDateMessage=""
                                showTodayButton
                                disableFuture
                                onChange={(date: MaterialUiPickersDate) => {
                                    props.onEndTimeChanged(date);
                                }}
                            />
                        </div>
                    </div>
                    {props.inputsError && (
                        <div className={classes.inputErrors}>
                            {props.inputsError}
                        </div>
                    )}
                </div>

                {isTabletOrLarger ? (
                    <Button
                        className={clsx(classes.input, classes.button)}
                        variant="contained"
                        color="default"
                        disabled={Boolean(!props.enablePrevButton || props.inputsError)}
                        onClick={props.onPrevButtonClicked}
                    >
                        {capitalizeAll(trans('label.prev'))}
                    </Button>
                ) : (
                    <IconButton
                        disabled={Boolean(!props.enableNextButton || props.inputsError)}
                        onClick={props.onPrevButtonClicked}
                    >
                        <Icon>arrow_back</Icon>
                    </IconButton>
                )}

                {isTabletOrLarger ? (
                    <Button
                        className={clsx(classes.input)}
                        variant="contained"
                        color="default"
                        disabled={Boolean(!props.enablePrevButton || props.inputsError)}
                        onClick={props.onNextButtonClicked}
                    >
                        {capitalizeAll(trans('label.next'))}
                    </Button>
                ) : (
                    <IconButton
                        disabled={Boolean(!props.enableNextButton || props.inputsError)}
                        onClick={props.onNextButtonClicked}
                    >
                        <Icon>arrow_forward</Icon>
                    </IconButton>
                )}
            </div>
            <div className={classes.rightControls} />
        </div>
    );
};
